import React, { useEffect, useState } from 'react';
import styled from 'styled-components';
import { H2, Icon, Button, StatusBadge, toast } from '@tviklax/ui-components';
import { API_BASE_URL } from '../../shared/constants/env';

const apiOrigin = API_BASE_URL.replace(/\/graphql\/?$/, '');

interface ExceptionItem {
  id: number;
  documentName: string | null;
  reason: string | null;
  extractedData: string | null;
  status: 'pending' | 'resolved' | 'discarded';
  createdAt: string | null;
  resolvedAt: string | null;
}

const Page = styled.div`
  padding: ${({ theme }) => theme.spacing[6]};
  display: flex;
  flex-direction: column;
  gap: ${({ theme }) => theme.spacing[4]};
`;

const Subtitle = styled.p`
  color: ${({ theme }) => theme.colors.gray500};
  font-size: 14px;
  margin: 0;
`;

const Card = styled.div`
  border: 1px solid ${({ theme }) => theme.colors.gray200};
  border-radius: ${({ theme }) => theme.borderRadius.lg};
  background: white;
  overflow: hidden;
`;

const Row = styled.div`
  display: grid;
  grid-template-columns: 1fr 2fr auto auto auto;
  gap: ${({ theme }) => theme.spacing[4]};
  align-items: center;
  padding: ${({ theme }) => theme.spacing[4]};
  border-bottom: 1px solid ${({ theme }) => theme.colors.gray100};

  &:last-child {
    border-bottom: none;
  }
`;

const HeaderRow = styled(Row)`
  background: ${({ theme }) => theme.colors.gray0};
  font-size: 12px;
  font-weight: 600;
  color: ${({ theme }) => theme.colors.gray500};
  text-transform: uppercase;
  letter-spacing: 0.02em;
`;

const DocName = styled.div`
  font-weight: 600;
  font-size: 14px;
`;

const Reason = styled.div`
  font-size: 13px;
  color: ${({ theme }) => theme.colors.gray600};
`;

const Meta = styled.div`
  font-size: 12px;
  color: ${({ theme }) => theme.colors.gray400};
`;

const Empty = styled.div`
  padding: ${({ theme }) => theme.spacing[10]};
  text-align: center;
  color: ${({ theme }) => theme.colors.gray400};
`;

const Details = styled.pre`
  grid-column: 1 / -1;
  background: ${({ theme }) => theme.colors.gray0};
  border-radius: ${({ theme }) => theme.borderRadius.md};
  padding: ${({ theme }) => theme.spacing[3]};
  font-size: 12px;
  overflow-x: auto;
  white-space: pre-wrap;
  word-break: break-word;
  margin: 0;
`;

const statusVariant = (status: ExceptionItem['status']) => {
  if (status === 'resolved') return 'success' as const;
  if (status === 'discarded') return 'neutral' as const;
  return 'warning' as const;
};

const formatDate = (value: string | null) => {
  if (!value) return '—';
  try {
    return new Date(value).toLocaleString();
  } catch {
    return value;
  }
};

export const ExceptionQueuePage: React.FC = () => {
  const [items, setItems] = useState<ExceptionItem[]>([]);
  const [loading, setLoading] = useState(true);
  const [expandedId, setExpandedId] = useState<number | null>(null);
  const [statusFilter, setStatusFilter] = useState<'pending' | 'all'>('pending');

  const load = async () => {
    setLoading(true);
    try {
      const query = statusFilter === 'pending' ? '?status=pending' : '';
      const response = await fetch(`${apiOrigin}/api/v1/exceptions${query}`);
      if (!response.ok) throw new Error(`Failed to load (${response.status})`);
      const data = await response.json();
      setItems(data);
    } catch (error) {
      toast({
        title: "Couldn't load the exception queue",
        description: error instanceof Error ? error.message : 'Unknown error',
        variant: 'destructive',
      });
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    load();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [statusFilter]);

  const updateStatus = async (id: number, status: 'resolved' | 'discarded') => {
    try {
      const response = await fetch(`${apiOrigin}/api/v1/exceptions/${id}`, {
        method: 'PATCH',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ status }),
      });
      if (!response.ok) throw new Error(`Update failed (${response.status})`);
      toast({ title: status === 'resolved' ? 'Marked as resolved' : 'Discarded' });
      load();
    } catch (error) {
      toast({
        title: "Couldn't update this entry",
        description: error instanceof Error ? error.message : 'Unknown error',
        variant: 'destructive',
      });
    }
  };

  const prettyData = (raw: string | null) => {
    if (!raw) return '—';
    try {
      return JSON.stringify(JSON.parse(raw), null, 2);
    } catch {
      return raw;
    }
  };

  return (
    <Page>
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-end' }}>
        <div>
          <H2 icon={<Icon name="AlertTriangle" size={20} color="#f86400" />}>Exception Queue</H2>
          <Subtitle>Invoices routed here from Import Invoice review because the extraction needs manual correction.</Subtitle>
        </div>
        <div style={{ display: 'flex', gap: '8px' }}>
          <Button variant={statusFilter === 'pending' ? 'default' : 'outline'} onClick={() => setStatusFilter('pending')}>
            Pending
          </Button>
          <Button variant={statusFilter === 'all' ? 'default' : 'outline'} onClick={() => setStatusFilter('all')}>
            All
          </Button>
        </div>
      </div>

      <Card>
        <HeaderRow>
          <div>Document</div>
          <div>Reason</div>
          <div>Status</div>
          <div>Received</div>
          <div>Actions</div>
        </HeaderRow>
        {loading && <Empty>Loading…</Empty>}
        {!loading && items.length === 0 && <Empty>Nothing here right now.</Empty>}
        {!loading &&
          items.map((item) => (
            <React.Fragment key={item.id}>
              <Row>
                <DocName>{item.documentName || 'Untitled document'}</DocName>
                <Reason>{item.reason || '—'}</Reason>
                <StatusBadge variant={statusVariant(item.status)} label={item.status} />
                <Meta>{formatDate(item.createdAt)}</Meta>
                <div style={{ display: 'flex', gap: '6px' }}>
                  <Button
                    variant="outline"
                    onClick={() => setExpandedId(expandedId === item.id ? null : item.id)}
                  >
                    {expandedId === item.id ? 'Hide' : 'View'}
                  </Button>
                  {item.status === 'pending' && (
                    <>
                      <Button variant="outline" onClick={() => updateStatus(item.id, 'resolved')}>
                        Resolve
                      </Button>
                      <Button variant="outline" onClick={() => updateStatus(item.id, 'discarded')}>
                        Discard
                      </Button>
                    </>
                  )}
                </div>
              </Row>
              {expandedId === item.id && (
                <Row>
                  <Details>{prettyData(item.extractedData)}</Details>
                </Row>
              )}
            </React.Fragment>
          ))}
      </Card>
    </Page>
  );
};
